import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getAdminEvents,
  getAdminEvent,
  createEvent,
  updateEvent,
  deleteEvent,
  toggleEventRegistration,
  AdminEventFilters,
  CreateEventPayload,
  UpdateEventPayload,
} from '../api/events.api';
import { Event } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import DeleteEventModal from '../components/DeleteEventModal';

const AdminEventsPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [registrationFilter, setRegistrationFilter] = useState<boolean | null>(null);
  const [activeFilter, setActiveFilter] = useState<boolean | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; eventId: string | null; eventName: string }>({
    show: false,
    eventId: null,
    eventName: '',
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    location: '',
    bannerUrl: '',
    status: '',
    registrationOpen: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [isFilterChanging, setIsFilterChanging] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const filters: AdminEventFilters = {
        page: currentPage,
        limit: 10,
      };
      
      if (statusFilter) {
        filters.status = statusFilter;
      }
      if (registrationFilter !== null) {
        filters.registrationOpen = registrationFilter;
      }
      if (activeFilter !== null) {
        filters.isActive = activeFilter;
      }
      if (searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }

      const response = await getAdminEvents(filters);
      
      if (response && response.data) {
        if (response.data.events) {
          setEvents(Array.isArray(response.data.events) ? response.data.events : []);
          setTotalPages(response.data.totalPages || 1);
          setTotal(response.data.total || 0);
        } else {
          setEvents([]);
          setTotalPages(1);
          setTotal(0);
        }
      } else {
        setEvents([]);
        setTotalPages(1);
        setTotal(0);
      }
    } catch (err: any) {
      console.error('Error fetching events:', err);
      setEvents([]);
      setTotalPages(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, registrationFilter, activeFilter, searchQuery]);

  useEffect(() => {
    if (isFilterChanging) {
      return;
    }
    fetchEvents();
  }, [currentPage, statusFilter, registrationFilter, activeFilter, searchQuery, fetchEvents, isFilterChanging]);

  const handleOpenCreateModal = () => {
    const now = new Date();
    const dateString = now.toISOString().split('T')[0];
    setFormData({
      name: '',
      description: '',
      startDate: dateString,
      endDate: '',
      location: '',
      bannerUrl: '',
      status: '',
      registrationOpen: false,
    });
    setErrors({});
    setShowCreateModal(true);
  };

  const handleOpenEditModal = async (event: Event) => {
    try {
      const response = await getAdminEvent(event.id);
      if (response.data) {
        const eventData = response.data;
        let startDateValue = '';
        let endDateValue = '';
        
        if (eventData.startDate) {
          if (eventData.startDate.includes('T')) {
            startDateValue = eventData.startDate.split('T')[0];
          } else {
            startDateValue = eventData.startDate.split(' ')[0];
          }
        }
        
        if (eventData.endDate) {
          if (eventData.endDate.includes('T')) {
            endDateValue = eventData.endDate.split('T')[0];
          } else {
            endDateValue = eventData.endDate.split(' ')[0];
          }
        }
        
        setFormData({
          name: eventData.name || '',
          description: eventData.description || '',
          startDate: startDateValue,
          endDate: endDateValue,
          location: eventData.location || '',
          bannerUrl: eventData.bannerUrl || '',
          status: eventData.status || '',
          registrationOpen: eventData.registrationOpen || false,
        });
        setErrors({});
        setEditingEvent(eventData);
      }
    } catch (err) {
      console.error('Error fetching event:', err);
    }
  };

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setEditingEvent(null);
    setFormData({
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      location: '',
      bannerUrl: '',
      status: '',
      registrationOpen: false,
    });
    setErrors({});
  };

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Event name is required.';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Event name must be at least 3 characters.';
    } else if (formData.name.trim().length > 200) {
      newErrors.name = 'Event name must not exceed 200 characters.';
    }

    if (formData.description && formData.description.trim().length > 5000) {
      newErrors.description = 'Description must not exceed 5000 characters.';
    }

    if (!formData.startDate || !formData.startDate.trim()) {
      newErrors.startDate = 'Start date is required.';
    } else {
      // Validate date format (should be YYYY-MM-DD for date input)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formData.startDate)) {
        newErrors.startDate = 'Invalid date format. Please select a date.';
      } else {
        const startDate = new Date(formData.startDate);
        if (isNaN(startDate.getTime())) {
          newErrors.startDate = 'Invalid date. Please select a valid date.';
        }
      }
    }

    if (formData.endDate && formData.endDate.trim() && formData.startDate && formData.startDate.trim()) {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formData.endDate)) {
        newErrors.endDate = 'Invalid date format. Please select a date.';
      } else {
        const startDate = new Date(formData.startDate);
        const endDate = new Date(formData.endDate);
        if (isNaN(endDate.getTime())) {
          newErrors.endDate = 'Invalid date. Please select a valid date.';
        } else if (endDate < startDate) {
          newErrors.endDate = 'End date must be after start date.';
        }
      }
    }

    if (formData.location && formData.location.length > 200) {
      newErrors.location = 'Location must not exceed 200 characters.';
    }

    if (formData.bannerUrl && formData.bannerUrl.length > 500) {
      newErrors.bannerUrl = 'Banner URL must not exceed 500 characters.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      // Validate and convert dates
      if (!formData.startDate || !formData.startDate.trim()) {
        setErrors({ startDate: 'Start date is required.' });
        setSaving(false);
        return;
      }

      const startDate = new Date(formData.startDate);
      if (isNaN(startDate.getTime())) {
        setErrors({ startDate: 'Invalid start date format.' });
        setSaving(false);
        return;
      }

      const startDateISO = startDate.toISOString();
      let endDateISO: string | undefined = undefined;

      if (formData.endDate && formData.endDate.trim()) {
        const endDate = new Date(formData.endDate);
        if (isNaN(endDate.getTime())) {
          setErrors({ endDate: 'Invalid end date format.' });
          setSaving(false);
          return;
        }
        if (endDate < startDate) {
          setErrors({ endDate: 'End date must be after start date.' });
          setSaving(false);
          return;
        }
        endDateISO = endDate.toISOString();
      }

      const payload: CreateEventPayload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        startDate: startDateISO,
        endDate: endDateISO,
        location: formData.location.trim() || undefined,
        bannerUrl: formData.bannerUrl.trim() || undefined,
        status: formData.status || undefined,
        registrationOpen: formData.registrationOpen,
      };

      console.log('Submitting event payload:', payload);
      console.log('Form data startDate:', formData.startDate);
      console.log('Converted startDateISO:', startDateISO);

      if (editingEvent) {
        await updateEvent(editingEvent.id, payload as UpdateEventPayload);
      } else {
        await createEvent(payload);
      }
      
      handleCloseModals();
      setTimeout(() => {
        fetchEvents();
      }, 500);
    } catch (err: any) {
      console.error('Error saving event:', err);
      console.error('Error response:', err.response);
      if (err.response?.status === 429) {
        setErrors({ 
          general: 'Too many requests. Please wait a moment and try again.' 
        });
      } else if (err.response?.status === 400) {
        const errorData = err.response?.data;
        console.log('Error data received:', errorData);
        console.log('Error data errors:', errorData?.errors);
        
        if (errorData?.errors) {
          // Map backend snake_case field names to frontend camelCase
          const mappedErrors: Record<string, string> = {};
          Object.keys(errorData.errors).forEach((key) => {
            // Map backend field names to frontend field names
            const fieldMap: Record<string, string> = {
              'start_date': 'startDate',
              'end_date': 'endDate',
              'banner_url': 'bannerUrl',
              'registration_open': 'registrationOpen',
              'is_active': 'isActive',
            };
            const frontendKey = fieldMap[key] || key;
            let errorMessage = errorData.errors[key];
            
            console.log(`Processing error for key: ${key}, frontendKey: ${frontendKey}, message:`, errorMessage);
            
            // Handle array of error messages (Joi sometimes returns arrays)
            if (Array.isArray(errorMessage)) {
              errorMessage = errorMessage[0] || errorMessage.join(', ');
            }
            
            // Clean up error messages that might contain duplicate field names
            // e.g., "startDate" "startDate" is required -> "Start date is required"
            if (typeof errorMessage === 'string') {
              // Remove duplicate quoted field names at the start (e.g., "startDate" "startDate" is required)
              errorMessage = errorMessage.replace(/^"[^"]+"\s*"[^"]+"\s*/i, '');
              // Remove single quoted field name at the start (e.g., "startDate" is required)
              errorMessage = errorMessage.replace(/^"[^"]+"\s*/i, '');
              // Trim whitespace
              errorMessage = errorMessage.trim();
              // Capitalize first letter
              if (errorMessage.length > 0) {
                errorMessage = errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);
              }
              // If message is empty or just "is required", add meaningful text
              if (!errorMessage || errorMessage.toLowerCase() === 'is required') {
                const fieldLabel = frontendKey.replace(/([A-Z])/g, ' $1').trim().toLowerCase();
                errorMessage = `${fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1)} is required`;
              }
            }
            
            mappedErrors[frontendKey] = errorMessage;
          });
          console.log('Mapped errors:', mappedErrors);
          setErrors(mappedErrors);
        } else {
          setErrors({ general: errorData?.message || 'Invalid data. Please check your inputs.' });
        }
      } else if (err.response?.status === 404) {
        setErrors({ general: 'Event not found. It may have been deleted.' });
      } else if (err.response?.status === 403) {
        setErrors({ general: 'You do not have permission to perform this action.' });
      } else {
        setErrors({ general: err.response?.data?.message || err.message || 'Failed to save event. Please try again.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.eventId) return;

    try {
      await deleteEvent(deleteModal.eventId);
      setDeleteModal({ show: false, eventId: null, eventName: '' });
      setTimeout(() => {
        fetchEvents();
      }, 500);
    } catch (err: any) {
      console.error('Error deleting event:', err);
      if (err.response?.status === 429) {
        alert('Too many requests. Please wait a moment and refresh the page.');
      } else {
        alert(err.response?.data?.message || 'Failed to delete event. Please try again.');
      }
    }
  };

  const handleToggleRegistration = async (eventId: string, currentStatus: boolean) => {
    try {
      await toggleEventRegistration(eventId, !currentStatus);
      setTimeout(() => {
        fetchEvents();
      }, 500);
    } catch (err: any) {
      console.error('Error toggling registration:', err);
      if (err.response?.status === 429) {
        alert('Too many requests. Please wait a moment and refresh the page.');
      } else {
        alert(err.response?.data?.message || 'Failed to toggle registration status. Please try again.');
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = async (filterType: string, value: string | boolean | null) => {
    setIsFilterChanging(true);
    
    if (filterType === 'status') {
      setStatusFilter(value as string | null);
    } else if (filterType === 'registration') {
      setRegistrationFilter(value as boolean | null);
    } else if (filterType === 'active') {
      setActiveFilter(value as boolean | null);
    }
    
    setCurrentPage(1);
    
    setLoading(true);
    try {
      const filters: AdminEventFilters = {
        page: 1,
        limit: 10,
      };
      
      if (filterType === 'status' && value) {
        filters.status = value as string;
      } else if (filterType === 'registration' && value !== null) {
        filters.registrationOpen = value as boolean;
      } else if (filterType === 'active' && value !== null) {
        filters.isActive = value as boolean;
      }
      
      if (searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }

      const response = await getAdminEvents(filters);
      
      if (response && response.data) {
        if (response.data.events) {
          setEvents(Array.isArray(response.data.events) ? response.data.events : []);
          setTotalPages(response.data.totalPages || 1);
          setTotal(response.data.total || 0);
        }
      }
    } catch (err: any) {
      console.error('Error fetching filtered events:', err);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setIsFilterChanging(false);
      }, 100);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setIsFilterChanging(true);
    setTimeout(() => {
      setIsFilterChanging(false);
    }, 100);
  };

  if (loading && events.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container py-5">
      <div className="mb-3">
        <Link to="/dashboard" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1"></i>
          Back to Dashboard
        </Link>
      </div>
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4 gap-3">
        <div>
          <h1 className="h3 section-title">EVENT MANAGEMENT</h1>
          <p className="text-muted-soft mb-0">Create, edit, and manage events</p>
        </div>
        <div className="create-notice-btn-container" style={{ flexShrink: 0, minWidth: 'fit-content', display: 'flex' }}>
          <button 
            className="btn btn-arena create-notice-btn" 
            onClick={handleOpenCreateModal}
            style={{ 
              display: 'inline-flex',
              visibility: 'visible',
              opacity: 1,
              position: 'relative',
              zIndex: 10,
              minWidth: '150px'
            }}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Create Event
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card card-surface mb-4">
        <div className="card-body">
          <form onSubmit={handleSearch} className="mb-3">
            <div className="row g-2">
              <div className="col-12 col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search events by name, description, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="col-12 col-md-6">
                <button type="submit" className="btn btn-outline-secondary w-100">
                  <i className="bi bi-search me-2"></i>
                  Search
                </button>
              </div>
            </div>
          </form>
          
          <div className="d-flex flex-column flex-md-row flex-wrap gap-2 align-items-start align-items-md-center">
            <span className="fw-semibold mb-2 mb-md-0">Filters:</span>
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className={`btn btn-sm ${statusFilter === null ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={() => handleFilterChange('status', null)}
              >
                All Status
              </button>
              <button
                type="button"
                className={`btn btn-sm ${statusFilter === 'current' ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={() => handleFilterChange('status', 'current')}
              >
                Current
              </button>
              <button
                type="button"
                className={`btn btn-sm ${statusFilter === 'upcoming' ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={() => handleFilterChange('status', 'upcoming')}
              >
                Upcoming
              </button>
              <button
                type="button"
                className={`btn btn-sm ${statusFilter === 'past' ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={() => handleFilterChange('status', 'past')}
              >
                Past
              </button>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className={`btn btn-sm ${registrationFilter === null ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={() => handleFilterChange('registration', null)}
              >
                All Registration
              </button>
              <button
                type="button"
                className={`btn btn-sm ${registrationFilter === true ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={() => handleFilterChange('registration', true)}
              >
                Registration Open
              </button>
              <button
                type="button"
                className={`btn btn-sm ${registrationFilter === false ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={() => handleFilterChange('registration', false)}
              >
                Registration Closed
              </button>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className={`btn btn-sm ${activeFilter === null ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={() => handleFilterChange('active', null)}
              >
                All Events
              </button>
              <button
                type="button"
                className={`btn btn-sm ${activeFilter === true ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={() => handleFilterChange('active', true)}
              >
                Active
              </button>
              <button
                type="button"
                className={`btn btn-sm ${activeFilter === false ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={() => handleFilterChange('active', false)}
              >
                Inactive
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Events List */}
      <div className="card card-surface">
        <div className="card-body">
          {loading ? (
            <LoadingSpinner />
          ) : events.length === 0 ? (
            <div className="alert alert-secondary mb-0">No events found.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th>Registration</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id}>
                      <td className="fw-semibold" style={{ maxWidth: '200px', wordBreak: 'break-word' }}>
                        {event.name}
                      </td>
                      <td>{event.startDate ? new Date(event.startDate).toLocaleDateString() : 'N/A'}</td>
                      <td>{event.endDate ? new Date(event.endDate).toLocaleDateString() : 'N/A'}</td>
                      <td style={{ maxWidth: '150px', wordBreak: 'break-word' }}>{event.location || 'N/A'}</td>
                      <td>
                        <span className={`badge ${event.status === 'Registration Open' ? 'bg-success' : event.status === 'Coming Soon' ? 'bg-info' : event.status === 'Ongoing' ? 'bg-primary' : event.status === 'Completed' ? 'bg-secondary' : 'bg-warning'} badge-status`}>
                          {event.status || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${event.registrationOpen ? 'bg-success' : 'bg-secondary'} badge-status`}>
                          {event.registrationOpen ? 'OPEN' : 'CLOSED'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${event.isActive !== false ? 'bg-success' : 'bg-danger'} badge-status`}>
                          {event.isActive !== false ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          {!event.registrationOpen && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success"
                              onClick={() => handleToggleRegistration(event.id, event.registrationOpen || false)}
                              title="Open Registration"
                            >
                              <i className="bi bi-unlock"></i>
                            </button>
                          )}
                          {event.registrationOpen && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => handleToggleRegistration(event.id, event.registrationOpen || false)}
                              title="Close Registration"
                            >
                              <i className="bi bi-lock"></i>
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleOpenEditModal(event)}
                            title="Edit Event"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() =>
                              setDeleteModal({
                                show: true,
                                eventId: event.id,
                                eventName: event.name,
                              })
                            }
                            title="Delete Event"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav aria-label="Page navigation" className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
            </li>
            {[...Array(totalPages)].map((_, i) => (
              <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => handlePageChange(i + 1)}>{i + 1}</button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>Next</button>
            </li>
          </ul>
          <div className="text-center mt-2 text-muted-soft small">
            Showing {events.length} of {total} event{total !== 1 ? 's' : ''}
          </div>
        </nav>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingEvent) && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={handleCloseModals}
            style={{ zIndex: 1040 }}
          ></div>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1050, position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', overflow: 'auto' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" style={{ margin: '1rem', maxWidth: '700px' }}>
              <div className="modal-content card-surface" style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
                <div className="modal-header border-bottom">
                  <h5 className="modal-title">{editingEvent ? 'Edit Event' : 'Create Event'}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModals}
                    aria-label="Close"
                  ></button>
                </div>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                  <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
                    {errors.general && (
                      <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        {errors.general}
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => {
                            const newErrors = { ...errors };
                            delete newErrors.general;
                            setErrors(newErrors);
                          }}
                          aria-label="Close"
                        ></button>
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label">
                        Event Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        disabled={saving}
                        maxLength={200}
                        placeholder="Enter event name"
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                      <div className="form-text">3-200 characters</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Description
                      </label>
                      <textarea
                        className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                        rows={5}
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        disabled={saving}
                        maxLength={5000}
                        placeholder="Enter event description"
                      />
                      {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                      <div className="form-text">Max 5000 characters</div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Start Date <span className="text-danger">*</span>
                          </label>
                          <input
                            type="date"
                            className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                            value={formData.startDate || ''}
                            onChange={(e) => {
                              console.log('Start date changed:', e.target.value);
                              handleChange('startDate', e.target.value);
                            }}
                            disabled={saving}
                            required
                          />
                          {errors.startDate && <div className="invalid-feedback">{errors.startDate}</div>}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            End Date
                          </label>
                          <input
                            type="date"
                            className={`form-control ${errors.endDate ? 'is-invalid' : ''}`}
                            value={formData.endDate || ''}
                            onChange={(e) => {
                              console.log('End date changed:', e.target.value);
                              handleChange('endDate', e.target.value);
                            }}
                            disabled={saving}
                            min={formData.startDate || undefined}
                          />
                          {errors.endDate && <div className="invalid-feedback">{errors.endDate}</div>}
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Location
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.location ? 'is-invalid' : ''}`}
                        value={formData.location}
                        onChange={(e) => handleChange('location', e.target.value)}
                        disabled={saving}
                        maxLength={200}
                        placeholder="Enter event location"
                      />
                      {errors.location && <div className="invalid-feedback">{errors.location}</div>}
                      <div className="form-text">Max 200 characters</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Banner URL
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.bannerUrl ? 'is-invalid' : ''}`}
                        value={formData.bannerUrl}
                        onChange={(e) => handleChange('bannerUrl', e.target.value)}
                        disabled={saving}
                        maxLength={500}
                        placeholder="Enter banner image URL"
                      />
                      {errors.bannerUrl && <div className="invalid-feedback">{errors.bannerUrl}</div>}
                      <div className="form-text">Max 500 characters</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Status
                      </label>
                      <select
                        className="form-control"
                        value={formData.status}
                        onChange={(e) => handleChange('status', e.target.value)}
                        disabled={saving}
                      >
                        <option value="">Select status</option>
                        <option value="Registration Open">Registration Open</option>
                        <option value="Coming Soon">Coming Soon</option>
                        <option value="Ongoing">Ongoing</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>

                    <div className="mb-3">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="registrationOpen"
                          checked={formData.registrationOpen}
                          onChange={(e) => handleChange('registrationOpen', e.target.checked)}
                          disabled={saving}
                        />
                        <label className="form-check-label" htmlFor="registrationOpen">
                          Registration Open
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer border-top d-flex flex-column flex-sm-row gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-secondary modal-footer-btn"
                      onClick={handleCloseModals}
                      disabled={saving}
                      style={{
                        display: 'inline-flex',
                        visibility: 'visible',
                        opacity: 1,
                        minWidth: '100px'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-arena modal-footer-btn" 
                      disabled={saving}
                      style={{
                        display: 'inline-flex',
                        visibility: 'visible',
                        opacity: 1,
                        minWidth: '150px'
                      }}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        editingEvent ? 'Update Event' : 'Create Event'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      <DeleteEventModal
        show={deleteModal.show}
        eventName={deleteModal.eventName}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal({ show: false, eventId: null, eventName: '' })}
      />
    </div>
  );
};

export default AdminEventsPage;
